import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Chip,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Switch,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  Refresh,
  Clear,
  Save,
  Cancel,
  School,
  FilterList,
  Link as LinkIcon,
  LinkOff as UnlinkIcon,
  AddCircle,
  RemoveCircle
} from '@mui/icons-material';

const Niveaux = () => {
  const [data, setData] = useState([]);
  const [annees, setAnnees] = useState([]);
  const [filieres, setFilieres] = useState([]);
  const [filiereNiveaux, setFiliereNiveaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Filtres en cascade
  const [filtreAnnee, setFiltreAnnee] = useState('all');
  const [filtreFiliere, setFiltreFiliere] = useState('all');
  
  // États pour le modal d'ajout/modification
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    niveau_id: '',
    code: '',
    intitule: '',
    description: '',
    ordre: 0,
    annee_id: '',
    filiere_id: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // ⭐ ÉTATS POUR L'AFFECTATION RAPIDE
  const [affectationModalOpen, setAffectationModalOpen] = useState(false);
  const [niveauAaffecter, setNiveauAaffecter] = useState(null);
  const [filiereAffectation, setFiliereAffectation] = useState('');

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  useEffect(() => {
    loadData();
    loadAnnees();
    loadFilieres();
    loadFiliereNiveaux();
  }, []);

  // Cascade: année → filière select
  const filteredFilieres = filtreAnnee === 'all'
    ? filieres
    : filieres.filter(f => f.annee_id === filtreAnnee);

  const loadData = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      
      console.log('🔄 [Niveaux] Chargement des données...');
      
      const niveauxRes = await axios.get('/api/niveaux', { headers });
      
      console.log(`📊 [Niveaux] ${niveauxRes.data?.length || 0} niveau(x) trouvé(s)`);
      
      setData(niveauxRes.data || []);
      setError('');
    } catch (err) {
      console.error('❌ [Niveaux] Erreur:', err);
      setError('Impossible de charger les niveaux');
    } finally {
      setLoading(false);
    }
  };

  const loadAnnees = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get('/api/annees', { headers });
      setAnnees(res.data || []);
    } catch (err) {
      console.error('Erreur chargement années:', err);
    }
  };

  const loadFilieres = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get('/api/filieres', { headers });
      setFilieres(res.data || []);
      console.log('📊 Filières chargées:', res.data?.length || 0);
    } catch (err) {
      console.error('❌ Erreur chargement filières:', err);
    }
  };

  const loadFiliereNiveaux = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get('/api/filiere-niveaux', { headers });
      setFiliereNiveaux(res.data || []);
      console.log('📊 FilièreNiveaux chargés:', res.data?.length || 0);
    } catch (err) {
      console.error('❌ Erreur chargement filiereNiveaux:', err);
    }
  };

  const generateNiveauId = () => {
    const maxId = data.reduce((max, item) => {
      const id = parseInt(item.niveau_id) || 0;
      return id > max ? id : max;
    }, 0);
    return String(maxId + 1);
  };

  const getFiliereLibelle = (filiereId) => {
    const filiere = filieres.find(f => f.filiere_id === filiereId);
    return filiere?.libelle || filiereId || 'Non définie';
  };

  const isNiveauAffected = (niveauId) => {
    return filiereNiveaux.some(fn => fn.niveau_id === niveauId);
  };

  const getFilieresForNiveau = (niveauId) => {
    const fn = filiereNiveaux.filter(f => f.niveau_id === niveauId);
    return fn.map(f => ({
      filiere_id: f.filiere_id,
      libelle: getFiliereLibelle(f.filiere_id),
      _id: f._id
    }));
  };

  const filteredData = data.filter(item => {
    const search = searchTerm.toLowerCase();
    const matchSearch = (item.intitule || '').toLowerCase().includes(search) ||
                        (item.code || '').toLowerCase().includes(search) ||
                        (item.niveau_id || '').toLowerCase().includes(search);
    
    if (!matchSearch) return false;
    
    if (filtreFiliere === 'all') return true;
    if (filtreFiliere === 'sans_filiere') {
      return !isNiveauAffected(item.niveau_id);
    }
    return filiereNiveaux.some(fn => 
      fn.niveau_id === item.niveau_id && fn.filiere_id === filtreFiliere
    );
  });

  // ========== MODAL AJOUT / MODIFICATION ==========
  const handleOpenModal = (mode, item = null) => {
    setModalMode(mode);
    setError('');
    setFormErrors({});
    setSubmitting(false);
    
    if (item) {
      console.log('📝 Édition du niveau:', item);
      
      const fn = filiereNiveaux.find(f => f.niveau_id === item.niveau_id);
      
      setSelectedItem(item);
      setFormData({
        niveau_id: item.niveau_id || '',
        code: item.code || '',
        intitule: item.intitule || '',
        description: item.description || '',
        ordre: item.ordre || 0,
        annee_id: item.annee_id || '',
        filiere_id: fn?.filiere_id || ''
      });
    } else {
      const newId = generateNiveauId();
      setFormData({
        niveau_id: newId,
        code: '',
        intitule: '',
        description: '',
        ordre: parseInt(newId) || 0,
        annee_id: '',
        filiere_id: ''
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedItem(null);
    setFormErrors({});
    setSubmitting(false);
  };

  // ========== GESTION DES AFFECTATIONS ==========
  
  // ⭐ Ouvrir le modal d'affectation rapide
  const handleOpenAffectationModal = (niveau) => {
    setNiveauAaffecter(niveau);
    setFiliereAffectation('');
    setAffectationModalOpen(true);
  };

  // ⭐ Fermer le modal d'affectation
  const handleCloseAffectationModal = () => {
    setAffectationModalOpen(false);
    setNiveauAaffecter(null);
    setFiliereAffectation('');
  };

  // ⭐ Affecter un niveau à une filière
  const affecterNiveauFiliere = async (niveauId, filiereId) => {
    try {
      const headers = getAuthHeaders();
      
      const existing = filiereNiveaux.find(fn => 
        fn.niveau_id === niveauId && fn.filiere_id === filiereId
      );
      
      if (existing) {
        setError('Cette association existe déjà');
        return false;
      }
      
      const data = {
        filiere_id: filiereId,
        niveau_id: niveauId
      };
      
      await axios.post('/api/filiere-niveaux', data, { headers });
      console.log('✅ Association créée avec succès');
      await loadFiliereNiveaux();
      setSuccessMessage('Niveau affecté avec succès à la filière');
      return true;
    } catch (err) {
      console.error('❌ Erreur lors de l\'affectation:', err);
      setError(err.response?.data?.error || 'Erreur lors de l\'affectation');
      return false;
    }
  };

  // ⭐ Désaffecter un niveau d'une filière
  const desaffecterNiveauFiliere = async (associationId, niveauLibelle, filiereLibelle) => {
    if (!window.confirm(`Désaffecter "${niveauLibelle}" de la filière "${filiereLibelle}" ?`)) return;
    
    try {
      const headers = getAuthHeaders();
      
      await axios.delete(`/api/filiere-niveaux/${associationId}`, { headers });
      console.log('✅ Association supprimée avec succès');
      await loadFiliereNiveaux();
      setSuccessMessage(`Niveau désaffecté de la filière ${filiereLibelle}`);
    } catch (err) {
      console.error('❌ Erreur lors de la désaffectation:', err);
      setError(err.response?.data?.error || 'Erreur lors de la désaffectation');
    }
  };

  // ⭐ Soumettre l'affectation rapide
  const handleAffectationSubmit = async () => {
    if (!niveauAaffecter || !filiereAffectation) {
      setError('Veuillez sélectionner une filière');
      return;
    }
    
    const success = await affecterNiveauFiliere(niveauAaffecter.niveau_id, filiereAffectation);
    if (success) {
      handleCloseAffectationModal();
    }
  };

  // ========== CRUD PRINCIPAL ==========
  const handleSubmit = async () => {
    const errors = {};
    if (!formData.intitule) errors.intitule = 'L\'intitulé est obligatoire';
    if (!formData.code) errors.code = 'Le code est obligatoire';
    if (!formData.niveau_id) errors.niveau_id = 'L\'ID est obligatoire';
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      setSubmitting(true);
      setLoading(true);
      const headers = getAuthHeaders();
      
      const dataToSend = {
        niveau_id: formData.niveau_id || generateNiveauId(),
        code: formData.code.toUpperCase(),
        intitule: formData.intitule,
        description: formData.description || '',
        ordre: parseInt(formData.ordre) || 0
      };
      
      console.log('📤 Données à envoyer:', dataToSend);
      
      let createdNiveauId = formData.niveau_id;
      
      if (modalMode === 'add') {
        console.log('➕ Ajout d\'un nouveau niveau');
        const response = await axios.post('/api/niveaux', dataToSend, { headers });
        console.log('✅ Réponse ajout:', response.data);
        createdNiveauId = response.data.data?.niveau_id || formData.niveau_id;
        setSuccessMessage('Niveau ajouté avec succès');
      } else {
        const idToUpdate = selectedItem?.niveau_id || selectedItem?._id || formData.niveau_id;
        console.log('📝 Mise à jour du niveau ID:', idToUpdate);
        
        const response = await axios.put(`/api/niveaux/${idToUpdate}`, dataToSend, { headers });
        console.log('✅ Réponse mise à jour:', response.data);
        createdNiveauId = idToUpdate;
        setSuccessMessage('Niveau modifié avec succès');
      }
      
      // Si une filière est sélectionnée, affecter le niveau
      if (formData.filiere_id) {
        await affecterNiveauFiliere(createdNiveauId, formData.filiere_id);
      }
      
      await loadData();
      handleCloseModal();
    } catch (err) {
      console.error('❌ Erreur complète:', err);
      
      let errorMessage = 'Erreur lors de l\'opération';
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      if (errorMessage.includes('duplicate') || errorMessage.includes('unique')) {
        errorMessage = 'Ce code de niveau existe déjà. Veuillez en utiliser un autre.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Supprimer définitivement ${item.intitule} ?`)) return;
    try {
      // Supprimer d'abord les associations
      const associations = filiereNiveaux.filter(fn => fn.niveau_id === item.niveau_id);
      for (const assoc of associations) {
        await axios.delete(`/api/filiere-niveaux/${assoc._id}`, { headers: getAuthHeaders() });
      }
      
      await axios.delete(`/api/niveaux/${item.niveau_id}`, { headers: getAuthHeaders() });
      setSuccessMessage('Niveau supprimé avec succès');
      await loadData();
      await loadFiliereNiveaux();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      if (field === 'code') {
        newData.code = value.toUpperCase();
      }
      
      if (field === 'ordre') {
        newData.ordre = parseInt(value) || 0;
      }
      
      return newData;
    });
    
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFiltreFiliereChange = (event) => {
    setFiltreFiliere(event.target.value);
    setPage(0);
  };

  if (loading && data.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const countByFiliere = {};
  filiereNiveaux.forEach(fn => {
    const filiereId = fn.filiere_id || 'sans_filiere';
    countByFiliere[filiereId] = (countByFiliere[filiereId] || 0) + 1;
  });

  return (
    <Box sx={{ p: 3 }}>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
        message={error}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />

      {/* En-tête */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ color: '#1f2937' }}>
            📚 Gestion des Niveaux
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filteredData.length} niveau(x) trouvé(s)
            {filtreFiliere !== 'all' && filtreFiliere !== 'sans_filiere' && ` - Filière: ${getFiliereLibelle(filtreFiliere)}`}
            {filtreFiliere === 'sans_filiere' && ' - Non affectés'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={() => { loadData(); loadFiliereNiveaux(); }}>
            Actualiser
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenModal('add')}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            Nouveau Niveau
          </Button>
        </Stack>
      </Box>

      {/* Filtres en cascade */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList color="action" />
            <Typography variant="body2" fontWeight="500" color="text.secondary">
              Filtres :
            </Typography>
          </Box>
          
          {/* Filtre Année */}
          <FormControl sx={{ minWidth: 180 }} size="small">
            <InputLabel>Année</InputLabel>
            <Select
              value={filtreAnnee}
              label="Année"
              onChange={(e) => { setFiltreAnnee(e.target.value); setFiltreFiliere('all'); setPage(0); }}
            >
              <MenuItem value="all">Toutes les années</MenuItem>
              {annees.map((annee) => (
                <MenuItem key={annee._id || annee.annee_id} value={annee.annee_id}>
                  {annee.intitule} {annee.estActive ? '⭐' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Filtre Filière */}
          <FormControl sx={{ minWidth: 250 }} size="small">
            <InputLabel>Filière</InputLabel>
            <Select
              value={filtreFiliere}
              onChange={handleFiltreFiliereChange}
              label="Filière"
            >
              <MenuItem value="all">
                <Stack direction="row" spacing={1} alignItems="center">
                  <span>Toutes les filières</span>
                  <Chip label={data.length} size="small" sx={{ height: 18, fontSize: '0.6rem' }} />
                </Stack>
              </MenuItem>
              <MenuItem value="sans_filiere">
                <Stack direction="row" spacing={1} alignItems="center">
                  <span>🔴 Non affectés</span>
                  <Chip label={data.filter(n => !isNiveauAffected(n.niveau_id)).length} size="small" sx={{ height: 18, fontSize: '0.6rem' }} />
                </Stack>
              </MenuItem>
              <Divider />
              {filteredFilieres.map((filiere) => {
                const count = countByFiliere[filiere.filiere_id] || 0;
                return (
                  <MenuItem key={filiere.filiere_id} value={filiere.filiere_id}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>{filiere.libelle}</span>
                      <Chip 
                        label={count} 
                        size="small" 
                        sx={{ height: 18, fontSize: '0.6rem' }} 
                        color={count > 0 ? 'primary' : 'default'}
                      />
                    </Stack>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          
          {(filtreAnnee !== 'all' || filtreFiliere !== 'all') && (
            <Button 
              size="small" 
              variant="outlined" 
              onClick={() => { setFiltreAnnee('all'); setFiltreFiliere('all'); }}
              startIcon={<Clear fontSize="small" />}
            >
              Effacer les filtres
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Recherche */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <TextField
          fullWidth
          placeholder="Rechercher par intitulé ou code..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Paper>

      {/* Tableau */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: 'auto' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ background: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Code</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Intitulé</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Ordre</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Filière(s)</TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.length > 0 ? (
              filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => {
                const filieresAssociees = getFilieresForNiveau(item.niveau_id);
                const estAffecte = filieresAssociees.length > 0;
                const filieresDisponibles = filieres.filter(f => 
                  !filieresAssociees.some(assoc => assoc.filiere_id === f.filiere_id)
                );
                
                return (
                  <TableRow key={item._id || item.niveau_id} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {item.niveau_id}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={item.code || '-'} 
                        size="small" 
                        sx={{ bgcolor: '#f3e8ff', color: '#7c3aed' }} 
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{item.intitule}</TableCell>
                    <TableCell>{item.ordre}</TableCell>
                    <TableCell>
                      {estAffecte ? (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {filieresAssociees.map((filiere) => (
                            <Chip 
                              key={filiere._id}
                              label={filiere.libelle} 
                              size="small" 
                              sx={{ bgcolor: '#dbeafe', color: '#1e40af' }}
                              onDelete={() => desaffecterNiveauFiliere(
                                filiere._id, 
                                item.intitule, 
                                filiere.libelle
                              )}
                              deleteIcon={<UnlinkIcon fontSize="small" />}
                            />
                          ))}
                        </Stack>
                      ) : (
                        <Chip 
                          label="Non affecté" 
                          size="small" 
                          sx={{ bgcolor: '#fee2e2', color: '#991b1b' }} 
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap">
                        {/* ⭐ BOUTON AFFECTER */}
                        {filieresDisponibles.length > 0 && (
                          <Tooltip title="Affecter à une filière">
                            <IconButton 
                              size="small" 
                              color="success"
                              onClick={() => handleOpenAffectationModal(item)}
                              sx={{ 
                                bgcolor: '#f0fdf4',
                                '&:hover': { bgcolor: '#dcfce7' }
                              }}
                            >
                              <LinkIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Modifier">
                          <IconButton size="small" color="warning" onClick={() => handleOpenModal('edit', item)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton size="small" color="error" onClick={() => handleDelete(item)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">
                    {filtreFiliere !== 'all' && filtreFiliere !== 'sans_filiere' 
                      ? `Aucun niveau trouvé pour la filière ${getFiliereLibelle(filtreFiliere)}`
                      : filtreFiliere === 'sans_filiere'
                        ? 'Aucun niveau non affecté trouvé'
                        : 'Aucun niveau trouvé'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredData.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        labelRowsPerPage="Lignes par page"
        sx={{ mt: 2 }}
      />

      {/* ========== MODAL AJOUT / MODIFICATION ========== */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <School />
            {modalMode === 'add' ? '➕ Ajouter un niveau' : '✏️ Modifier le niveau'}
            {modalMode === 'add' && (
              <Chip label="Nouveau" size="small" color="primary" sx={{ ml: 1 }} />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="ID Niveau *"
                  name="niveau_id"
                  value={formData.niveau_id || ''}
                  onChange={(e) => handleFormChange('niveau_id', e.target.value)}
                  margin="normal"
                  required
                  disabled={modalMode === 'edit'}
                  helperText={modalMode === 'add' ? 'ID unique du niveau' : 'Non modifiable'}
                  error={!!formErrors.niveau_id}
                />
                {formErrors.niveau_id && (
                  <Typography variant="caption" color="error">{formErrors.niveau_id}</Typography>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Code *"
                  name="code"
                  value={formData.code || ''}
                  onChange={(e) => handleFormChange('code', e.target.value)}
                  margin="normal"
                  required
                  placeholder="ex: 35"
                  helperText="Code unique du niveau (en majuscules)"
                  error={!!formErrors.code}
                />
                {formErrors.code && (
                  <Typography variant="caption" color="error">{formErrors.code}</Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Intitulé *"
                  name="intitule"
                  value={formData.intitule || ''}
                  onChange={(e) => handleFormChange('intitule', e.target.value)}
                  margin="normal"
                  required
                  placeholder="ex: M2 DCMD"
                  error={!!formErrors.intitule}
                />
                {formErrors.intitule && (
                  <Typography variant="caption" color="error">{formErrors.intitule}</Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description || ''}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  margin="normal"
                  placeholder="ex: Master 2 Développement Commercial et Marketing Digital"
                  multiline
                  rows={2}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Ordre"
                  name="ordre"
                  type="number"
                  value={formData.ordre || 0}
                  onChange={(e) => handleFormChange('ordre', e.target.value)}
                  margin="normal"
                  InputProps={{ inputProps: { min: 0 } }}
                  helperText="Ordre d'affichage"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Année Universitaire</InputLabel>
                  <Select
                    value={formData.annee_id || ''}
                    onChange={(e) => handleFormChange('annee_id', e.target.value)}
                    label="Année Universitaire"
                  >
                    <MenuItem value="">Non définie</MenuItem>
                    {annees.map((annee) => (
                      <MenuItem key={annee._id || annee.annee_id} value={annee.annee_id}>
                        {annee.intitule} {annee.estActive ? '⭐' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Affecter à une filière</InputLabel>
                  <Select
                    value={formData.filiere_id || ''}
                    onChange={(e) => handleFormChange('filiere_id', e.target.value)}
                    label="Affecter à une filière"
                  >
                    <MenuItem value="">Aucune filière</MenuItem>
                    {(formData.annee_id ? filieres.filter(f => f.annee_id === formData.annee_id) : filieres).map((filiere) => (
                      <MenuItem key={filiere.filiere_id} value={filiere.filiere_id}>
                        {filiere.libelle} - {filiere.intitule}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} startIcon={<Cancel />} disabled={submitting}>
            Annuler
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            startIcon={submitting ? <CircularProgress size={20} /> : <Save />} 
            sx={{ bgcolor: '#667eea' }}
            disabled={!formData.intitule || !formData.code || !formData.niveau_id || submitting}
          >
            {submitting ? 'Enregistrement...' : (modalMode === 'add' ? 'Ajouter' : 'Modifier')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========== MODAL D'AFFECTATION RAPIDE ========== */}
      <Dialog open={affectationModalOpen} onClose={handleCloseAffectationModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinkIcon color="primary" />
            Affecter le niveau à une filière
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Niveau :</strong> {niveauAaffecter?.intitule} ({niveauAaffecter?.code})
            </Typography>
            
            <FormControl fullWidth margin="normal">
              <InputLabel>Sélectionner une filière *</InputLabel>
              <Select
                value={filiereAffectation}
                onChange={(e) => setFiliereAffectation(e.target.value)}
                label="Sélectionner une filière *"
              >
                <MenuItem value="">Choisir une filière</MenuItem>
                {filieres.filter(f => 
                  !filiereNiveaux.some(fn => 
                    fn.niveau_id === niveauAaffecter?.niveau_id && fn.filiere_id === f.filiere_id
                  )
                ).map((filiere) => (
                  <MenuItem key={filiere.filiere_id} value={filiere.filiere_id}>
                    {filiere.libelle} - {filiere.intitule}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {filiereAffectation && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid #bbf7d0' }}>
                <Typography variant="subtitle2" fontWeight="bold" color="#166534">
                  ✅ Récapitulatif :
                </Typography>
                <Typography variant="body2">
                  📚 Niveau : {niveauAaffecter?.intitule}
                </Typography>
                <Typography variant="body2">
                  🎓 Filière : {getFiliereLibelle(filiereAffectation)}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAffectationModal} startIcon={<Cancel />}>
            Annuler
          </Button>
          <Button 
            onClick={handleAffectationSubmit} 
            variant="contained" 
            startIcon={<LinkIcon />}
            sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
            disabled={!filiereAffectation}
          >
            Affecter
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Niveaux;


